import { ReactComponent as BurgerIcon } from 'assets/svg/icons/BurgerMenu.svg'
import { ReactComponent as CloseIcon } from 'assets/svg/icons/Close.svg'
import Identicon from 'components/Identicon'
import { useConnection } from 'hooks/useConnection'
import { ReactNode, useEffect, useState } from 'react'
import { ChevronDown, ChevronUp } from 'react-feather'
import { animated, useSpring, useTransition } from 'react-spring'
import { useToggleWalletModal } from 'state/application/hooks'
import styled from 'styled-components'
import { shortenAddress } from 'utils'

import { StyledNavLink } from '.'
import NetworkSelector from './NetworkSelector'
import PortfolioLink from './PortfolioLink'
import { SchemaItemMultiple, useSchema } from './Schema'
import Web3Status from './Web3Status'

const BurgerButtonWrapper = styled.div`
  display: flex;
  padding: 8px;
  :hover {
    background-color: ${({ theme }) => theme.cardPrimary};
    border-radius: 8px;
  }
  width: 32px;
  height: 32px;
  justify-content: center;
  align-items: center;
`

export const BurgerButton = ({ opened = false, onClick }: { opened?: boolean; onClick?: () => void }) => {
  const t = useTransition(opened, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 50 },
  })

  return (
    <BurgerButtonWrapper onClick={onClick}>
      {t((styles, item, _, key) => (
        <animated.div style={styles} key={key}>
          {!opened ? <BurgerIcon /> : <CloseIcon />}
        </animated.div>
      ))}
    </BurgerButtonWrapper>
  )
}

const BurgerMenuOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ theme }) => theme.backgroundSurface};
`

const BurgerBlock = styled.div`
  padding-top: 20px;
  :first-child {
    padding-top: 60px;
  }
  & > * {
    padding: 0px 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const BurgerMenuContent = styled.div`
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  & > ${BurgerBlock}:not(:last-child)::after {
    content: '';
    border-bottom: 1px solid ${({ theme }) => theme.cardBorderPrimary};
    width: 100%;
    display: block;
    padding-bottom: 20px;
  }
`

const BurgerMenuSubLinksWrapper = styled.div<{ opened: boolean }>`
  max-height: ${({ opened }) => `${opened ? 'auto' : '0px'}`};
  overflow: hidden;
  transition-property: max-height, height;
  transition-duration: 0.3s;
`

const BurgerDivider = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  width: 100%;
`

interface BurgerLinkProps {
  id: string
  to: string
  isActive?: boolean
  label: ReactNode
  external?: boolean
  sublinks?: SchemaItemMultiple['sublinks']
  marginLeft?: string
  onClick?: () => void
}

const BurgerLink = ({ id, to, isActive, label, external, sublinks, marginLeft, onClick }: BurgerLinkProps) => {
  const [opened, setOpened] = useState(false)

  const hasSublinks = Array.isArray(sublinks) && sublinks.length > 0

  const switchOpened = () => {
    setOpened((prev) => !prev)
  }

  const handleClick = (e?: React.MouseEvent) => {
    if (hasSublinks) {
      e?.preventDefault()
      switchOpened()
      return
    }

    onClick && onClick()
  }

  return (
    <>
      <StyledNavLink
        onClick={handleClick}
        id={id}
        to={to}
        $isActive={isActive}
        target={external ? '_blank' : undefined}
        style={{
          marginLeft,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          height: '56px',
        }}
        width="100%"
      >
        <span>
          {label}
          {external && (
            <>
              {' '}
              <sup>↗</sup>
            </>
          )}
        </span>
        {hasSublinks && (opened ? <ChevronUp /> : <ChevronDown />)}
      </StyledNavLink>
      <BurgerMenuSubLinksWrapper opened={opened}>
        {hasSublinks &&
          sublinks.map(({ id, href, label }) => (
            <BurgerLink key={id} id={id} to={href} label={label} marginLeft="8px" onClick={onClick} />
          ))}
      </BurgerMenuSubLinksWrapper>
    </>
  )
}

const BurgerControlsWrapper = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: row;
  gap: 20px;
  align-items: flex-start;
  background: ${({ theme }) => theme.backgroundSurface};

  & > *:not(:last-child) {
    margin-bottom: 20px;
  }
`

const BurgerAccountWrapper = styled.div`
  display: flex;
  flex-direction: row;
  & > * {
    margin-right: 8px;
  }
  align-items: center;
  cursor: pointer;
`

export const BurgerAccount = ({ onClose }: { onClose?: () => void }) => {
  const { account } = useConnection()
  const toggleWalletModal = useToggleWalletModal()

  const handleClick = () => {
    onClose?.()
    toggleWalletModal()
  }

  return (
    <BurgerAccountWrapper onClick={handleClick}>
      <Identicon /> {account ? shortenAddress(account, 6) : <></>}
    </BurgerAccountWrapper>
  )
}

export const BurgerMenu = ({ show, onClose }: { show: boolean; onClose?: () => void }) => {
  const schema = useSchema()
  const { account } = useConnection()

  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [show])

  const props = useSpring({
    transform: show ? 'translateX(0)' : 'translateX(-100%)',
    from: { transform: 'translateX(-100%)' },
    config: { duration: 100 },
  })

  return (
    <animated.div
      style={{
        ...props,
        position: 'fixed',
        top: 24,
        left: 0,
        width: '100%',
        height: 'calc(100vh - 24px)',
        zIndex: 20,
        overflowY: show ? 'auto' : 'hidden',
        pointerEvents: show ? 'auto' : 'none',
        paddingTop: '8px',
      }}
    >
      <BurgerMenuOverlay />
      <BurgerMenuContent>
        <BurgerBlock>
          {schema.map((item) => {
            return item.to || item.href ? (
              <BurgerLink
                key={item.id}
                id={item.id}
                to={item.to ?? item.href ?? '#'}
                label={item.label}
                sublinks={item.sublinks}
                onClick={onClose}
              />
            ) : (
              <BurgerLink key={item.id} id={item.id} label={item.label} to="#" sublinks={item.sublinks} />
            )
          })}
        </BurgerBlock>
        <BurgerBlock>
          <BurgerControlsWrapper>
            <PortfolioLink onClick={onClose} />
            <div style={{ display: 'flex', gap: '8px' }}>
              {!account ? <Web3Status /> : <BurgerAccount />}
              <NetworkSelector menuAlignment="right" />
            </div>
          </BurgerControlsWrapper>
        </BurgerBlock>
      </BurgerMenuContent>
    </animated.div>
  )
}
